// Recover from stale-deploy chunk errors.
//
// Routes are lazy-loaded (import()). After a redeploy the hashed chunk names
// change, so a browser (or an out-of-date service worker) still holding the old
// app shell tries to import a chunk that no longer exists → the dynamic import
// throws "Failed to fetch dynamically imported module" and the page dies with no
// UI. This is the intermittent "page loading error" users hit right after a
// deploy — especially when redirected in from WapMeet for signing.
//
// The cure is to reload once — but a plain location.reload() is NOT enough for a
// service-worker PWA. This app registers a workbox SW that precaches the app
// shell and serves navigations from it (navigateFallback: '/index.html'). A
// reload is intercepted by the still-active OLD service worker, which hands back
// the SAME stale precached index.html → the same missing chunk → the reload
// never heals. So before reloading we must evict the service worker and its
// caches, forcing the reload to hit nginx (index.html: no-cache) and fetch the
// fresh shell + valid chunk hashes. VitePWA (autoUpdate) re-registers a fresh SW
// on the next load. The sessionStorage time-guard makes sure we recover AT MOST
// once per window, so a genuinely broken deploy (chunk still missing after the
// reload) can never spin into an infinite reload loop — it surfaces to the error
// boundary instead.

const RELOAD_KEY = 'wapsign:chunk-reload-at';
const RELOAD_WINDOW_MS = 10_000;

// Evict any registered service workers + Cache Storage, then reload. Exported so
// the error-boundary's manual "Reload" button recovers too (a bare reload would
// be re-served the stale precached shell by the SW). Best-effort: reloads even
// if eviction fails.
export async function hardReloadFromNetwork(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* eviction is best-effort; reload regardless */
  }
  window.location.reload();
}

const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|dynamically imported module/i;

export function isChunkLoadError(reason: unknown): boolean {
  if (!reason) return false;
  const msg = reason instanceof Error ? reason.message : String(reason);
  return CHUNK_ERROR_RE.test(msg);
}

// Reload once to pick up the fresh shell/chunks. Returns true if a reload was
// triggered, false if it was suppressed by the loop-guard (caller should then
// let the error surface normally).
export function recoverFromStaleChunk(): boolean {
  let last = 0;
  try { last = Number(sessionStorage.getItem(RELOAD_KEY) ?? '0'); } catch { /* storage blocked */ }
  if (Date.now() - last < RELOAD_WINDOW_MS) return false;
  try { sessionStorage.setItem(RELOAD_KEY, String(Date.now())); } catch { /* storage blocked */ }
  void hardReloadFromNetwork();
  return true;
}

// Wire up the global listeners. Call once at boot.
export function installChunkReloadHandler(): void {
  // Vite fires this specifically when import() can't load a chunk.
  window.addEventListener('vite:preloadError', (e) => {
    e.preventDefault();
    recoverFromStaleChunk();
  });
  // Safari/Firefox surface the same failure as a rejected promise instead of a
  // vite:preloadError, so catch those too — but only the chunk-load ones.
  window.addEventListener('unhandledrejection', (e) => {
    if (isChunkLoadError(e.reason)) recoverFromStaleChunk();
  });
}
